"use client";

import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Paperclip, X, Image, Check, ChevronLeft, Loader2 } from "lucide-react";
import { loadIssuePhoto } from "@/lib/issue-photo";
import { compressImageForEmail, getDataUrlSize } from "@/lib/image-utils";
import { format } from "date-fns";

interface Attachment {
  id: string;
  filename: string;
  mimeType: string;
  dataUrl: string;
  size: number;
}

interface AttachmentPickerProps {
  blockId?: Id<"blocks">;
  attachments: Attachment[];
  onAttachmentsChange: (attachments: Attachment[]) => void;
}

export function AttachmentPicker({
  blockId: initialBlockId,
  attachments,
  onAttachmentsChange,
}: AttachmentPickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState<Id<"blocks"> | undefined>(initialBlockId);

  // Fetch blocks with photos for selection
  const blocksWithPhotos = useQuery(api.issuePhotos.getBlocksWithPhotos);

  const photos = useQuery(
    api.issuePhotos.getByBlock,
    selectedBlockId ? { blockId: selectedBlockId } : "skip"
  );

  const [loadedPhotos, setLoadedPhotos] = useState<Record<string, string>>({});

  useEffect(() => {
    photos?.forEach(async (photo) => {
      if (!loadedPhotos[photo.path]) {
        const dataUrl = await loadIssuePhoto(photo.path);
        if (dataUrl) {
          setLoadedPhotos((prev) => ({ ...prev, [photo.path]: dataUrl }));
        }
      }
    });
  }, [photos, loadedPhotos]);

  const [compressing, setCompressing] = useState<string | null>(null);

  const toggleAttachment = async (photoPath: string) => {
    const dataUrl = loadedPhotos[photoPath];
    if (!dataUrl) return;

    const existingIndex = attachments.findIndex((a) => a.id === photoPath);
    if (existingIndex >= 0) {
      onAttachmentsChange(attachments.filter((_, i) => i !== existingIndex));
    } else {
      // Compress image for email (max 1200px, 70% quality)
      setCompressing(photoPath);
      try {
        const compressed = await compressImageForEmail(dataUrl);
        const size = getDataUrlSize(compressed);
        onAttachmentsChange([
          ...attachments,
          {
            id: photoPath,
            filename: `issue-photo-${attachments.length + 1}.jpg`,
            mimeType: "image/jpeg",
            dataUrl: compressed,
            size,
          },
        ]);
      } finally {
        setCompressing(null);
      }
    }
  };

  const removeAttachment = (id: string) => {
    onAttachmentsChange(attachments.filter((a) => a.id !== id));
  };

  const totalSize = attachments.reduce((sum, a) => sum + a.size, 0);
  const maxSize = 25 * 1024 * 1024; // 25MB Gmail limit

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Attachments</label>
        <Sheet open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          // Reset to block selection when closing if no initial block
          if (!isOpen && !initialBlockId) {
            setSelectedBlockId(undefined);
          }
        }}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm">
              <Paperclip className="h-4 w-4 mr-2" />
              Add from Block
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>
                {selectedBlockId ? (
                  <button
                    className="flex items-center gap-1 text-left"
                    onClick={() => setSelectedBlockId(undefined)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Select Photos
                  </button>
                ) : (
                  "Select Block"
                )}
              </SheetTitle>
            </SheetHeader>

            {/* Block Selection */}
            {!selectedBlockId && (
              <div className="mt-4 space-y-2">
                {blocksWithPhotos?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No blocks with photos found
                  </p>
                ) : (
                  blocksWithPhotos?.map((block) => (
                    <button
                      key={block._id}
                      className="w-full p-3 rounded-lg bg-muted hover:bg-muted/80 text-left"
                      onClick={() => setSelectedBlockId(block._id)}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium">
                          {format(block.date, "MMM d, yyyy")}
                        </p>
                        {block.station && (
                          <span className="text-xs bg-background px-2 py-0.5 rounded">
                            {block.station}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {block.shiftStart} - {block.shiftEnd} · {block.photoCount} photo{block.photoCount !== 1 ? "s" : ""}
                      </p>
                    </button>
                  ))
                )}
              </div>
            )}

            {/* Photo Selection */}
            {selectedBlockId && (
              <div className="grid grid-cols-2 gap-2 mt-4">
                {photos?.map((photo) => {
                  const isSelected = attachments.some((a) => a.id === photo.path);
                  return (
                    <div
                      key={photo._id}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer ${
                        isSelected ? "border-primary" : "border-transparent"
                      }`}
                      onClick={() => toggleAttachment(photo.path)}
                    >
                      {loadedPhotos[photo.path] ? (
                        <img
                          src={loadedPhotos[photo.path]}
                          alt="Issue"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted animate-pulse" />
                      )}
                      {compressing === photo.path && (
                        <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      )}
                      {isSelected && !compressing && (
                        <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
                          <Check className="h-3 w-3 text-primary-foreground" />
                        </div>
                      )}
                    </div>
                  );
                })}
                {photos?.length === 0 && (
                  <p className="col-span-2 text-sm text-muted-foreground text-center py-4">
                    No photos for this block
                  </p>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>

      {/* Selected attachments */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center justify-between p-2 rounded-lg bg-muted"
            >
              <div className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                <span className="text-sm">{attachment.filename}</span>
                <span className="text-xs text-muted-foreground">
                  ({Math.round(attachment.size / 1024)}KB)
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeAttachment(attachment.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Total: {Math.round(totalSize / 1024)}KB / {Math.round(maxSize / 1024 / 1024)}MB
          </p>
        </div>
      )}
    </div>
  );
}
